import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Switch,
  message,
  Popconfirm,
  Typography,
  Alert,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import api, { tradesAPI, getQuoteStreamUrl } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

/** 管理端行情 HTTP 兜底轮询（与 Trades 页同源逻辑） */
const ADMIN_QUOTE_HTTP_MS = Math.max(200, parseInt(process.env.REACT_APP_QUOTE_HTTP_POLL_MS || '2000', 10));

const normSymbol = (s) => String(s || '').trim().toUpperCase();

/** 盘前/盘后 price 可能为 0，退回昨收 */
const effectiveQuotePrice = (q) => {
  if (!q || q.error) return NaN;
  const p = q.price != null && q.price !== '' ? parseFloat(q.price) : NaN;
  if (!Number.isNaN(p) && p > 0) return p;
  const pc = q.preClose != null && q.preClose !== '' ? parseFloat(q.preClose) : NaN;
  if (!Number.isNaN(pc) && pc > 0) return pc;
  return NaN;
};

const SOURCE_TAG = {
  itick: { color: 'green', label: '实时(iTick)' },
  finnhub: { color: 'green', label: '实时(Finnhub)' },
  longbridge: { color: 'processing', label: '实时(LB)' },
  psbangu: { color: 'processing', label: '实时(澎博)' },
  psbangu_http: { color: 'processing', label: '准实时(澎博)' },
  eastmoney: { color: 'cyan', label: '准实时(东财)' },
  yahoo: { color: 'purple', label: 'Yahoo' },
  sina: { color: 'default', label: '延时 15min' },
};

const VipSignalsManage = () => {
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({
    trading: { title: '', description: '' },
  });
  const [loading, setLoading] = useState(false);
  const category = 'trading';
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [metaForm] = Form.useForm();
  const [savingMeta, setSavingMeta] = useState(false);
  const [quotes, setQuotes] = useState({});
  const quoteWsRef = useRef(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/vip-signals/admin', { params: { category: 'all' } });
      if (res.data.success) {
        setList(res.data.data.signals || []);
        if (res.data.data.meta) {
          setMeta(res.data.data.meta);
          metaForm.setFieldsValue(res.data.data.meta[category] || {});
        }
      }
    } catch (e) {
      message.error('获取信号列表失败');
    } finally {
      setLoading(false);
    }
  }, [category, metaForm]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    metaForm.setFieldsValue(meta[category] || {});
  }, [category, meta, metaForm]);

  const activeSymbols = useMemo(
    () => [
      ...new Set(
        list
          .filter((s) => s.status !== 'closed')
          .map((s) => normSymbol(s.symbol))
          .filter(Boolean)
      ),
    ],
    [list]
  );

  // WebSocket 实时推送 + 定时 HTTP 兜底
  useEffect(() => {
    if (activeSymbols.length === 0) {
      setQuotes({});
      return undefined;
    }

    let stopped = false;
    let reconnectTimer;

    const applyQuote = (sym, q) => {
      if (!q || q.error) return;
      if (Number.isNaN(effectiveQuotePrice(q))) return;
      setQuotes((prev) => ({ ...prev, [sym]: q }));
    };

    const fetchHttp = async () => {
      try {
        const res = await tradesAPI.getStockPrices(activeSymbols);
        if (stopped || !res.data?.success || !res.data?.data) return;
        for (const key of Object.keys(res.data.data)) {
          applyQuote(normSymbol(key), res.data.data[key]);
        }
      } catch (e) {
        console.error('VIP signals fetch stock prices error:', e);
      }
    };

    fetchHttp();

    const connectWs = () => {
      if (stopped) return;
      try {
        const ws = new WebSocket(getQuoteStreamUrl());
        quoteWsRef.current = ws;
        ws.onopen = () => {
          ws.send(JSON.stringify({ action: 'subscribe', symbols: activeSymbols }));
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === 'quote' && msg.data && msg.data.symbol) {
              applyQuote(normSymbol(msg.data.symbol), msg.data);
            }
          } catch (e) {
            console.warn('VIP signals quote WS', e);
          }
        };
        ws.onclose = () => {
          if (stopped) return;
          quoteWsRef.current = null;
          reconnectTimer = setTimeout(connectWs, 3000);
        };
        ws.onerror = () => {
          try {
            ws.close();
          } catch (_) {
            /* noop */
          }
        };
      } catch (e) {
        console.error('VIP signals quote WebSocket', e);
        reconnectTimer = setTimeout(connectWs, 5000);
      }
    };

    connectWs();

    const isWsAlive = () => quoteWsRef.current && quoteWsRef.current.readyState === WebSocket.OPEN;
    const httpTimer = setInterval(() => {
      if (stopped) return;
      if (document.hidden) return;
      if (isWsAlive()) return;
      fetchHttp();
    }, ADMIN_QUOTE_HTTP_MS);

    return () => {
      stopped = true;
      clearInterval(httpTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (quoteWsRef.current) {
        quoteWsRef.current.close();
        quoteWsRef.current = null;
      }
    };
  }, [activeSymbols.join('|')]);

  /** 持仓中优先用实时价；取不到则退回库里的 current_price */
  const getMarkPrice = (r) => {
    if (r.status === 'closed') return r.exitPrice != null ? Number(r.exitPrice) : NaN;
    const live = effectiveQuotePrice(quotes[normSymbol(r.symbol)]);
    if (!Number.isNaN(live)) return live;
    return r.currentPrice != null ? Number(r.currentPrice) : NaN;
  };

  /** 按后端同一公式重算，保证与前台展示一致 */
  const getPnl = (r) => {
    const mark = getMarkPrice(r);
    const entry = Number(r.entryPrice);
    if (Number.isNaN(mark) || !(entry > 0)) {
      return { amount: Number(r.pnlAmount) || 0, ratio: Number(r.pnlRatio) || 0 };
    }
    return {
      amount: (mark - entry) * (Number(r.quantity) || 0),
      ratio: ((mark - entry) / entry) * 100,
    };
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setModalOpen(true);
  };

  // destroyOnClose 下 Form 在 Modal 打开后才挂载，须在此回填
  useEffect(() => {
    if (!modalOpen) return;

    if (editing) {
      form.setFieldsValue({
        category: editing.category || category,
        symbol: editing.symbol,
        region: editing.region,
        status: editing.status,
        quantity: editing.quantity,
        entryPrice: editing.entryPrice,
        currentPrice: editing.currentPrice ?? null,
        exitPrice: editing.exitPrice ?? null,
        entryDate: editing.entryDate ? dayjs(editing.entryDate) : null,
        sortOrder: editing.sortOrder ?? 0,
        isActive: editing.isActive !== false,
        notes: editing.notes || '',
      });
      return;
    }

    form.resetFields();
    form.setFieldsValue({
      category,
      region: 'USA',
      status: 'active',
      isActive: true,
      sortOrder: 0,
      quantity: 100,
      entryDate: dayjs(),
    });
  }, [modalOpen, editing, form, category]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        entryDate: values.entryDate ? values.entryDate.toISOString() : new Date().toISOString(),
      };
      if (editing) {
        await api.put(`/vip-signals/admin/${editing.id}`, payload);
        message.success('已更新');
      } else {
        await api.post('/vip-signals/admin', payload);
        message.success('已创建');
      }
      setModalOpen(false);
      setEditing(null);
      fetchList();
    } catch (e) {
      if (e?.errorFields) return;
      message.error('保存失败：' + (e.response?.data?.message || e.message));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/vip-signals/admin/${id}`);
      message.success('已删除');
      fetchList();
    } catch (e) {
      message.error('删除失败');
    }
  };

  const saveMeta = async () => {
    try {
      setSavingMeta(true);
      const values = await metaForm.validateFields();
      await api.put(`/vip-signals/admin/meta/${category}`, values);
      message.success('页头配置已保存');
      fetchList();
    } catch (e) {
      if (e?.errorFields) return;
      message.error('保存失败');
    } finally {
      setSavingMeta(false);
    }
  };

  const filtered = list.filter((s) => s.category === category);

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '代码',
      dataIndex: 'symbol',
      width: 90,
      render: (v) => <Text strong>{v}</Text>,
    },
    { title: '市场', dataIndex: 'region', width: 70 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v) => (v === 'closed' ? <Tag>已平仓</Tag> : <Tag color="green">持仓中</Tag>),
    },
    {
      title: '入场价',
      dataIndex: 'entryPrice',
      width: 100,
      render: (v) => `$${Number(v).toFixed(2)}`,
    },
    {
      title: '现价/出场',
      width: 190,
      render: (_, r) => {
        const p = getMarkPrice(r);
        if (Number.isNaN(p)) return '—';

        const q = r.status !== 'closed' ? quotes[normSymbol(r.symbol)] : null;
        const tag = q && !Number.isNaN(effectiveQuotePrice(q)) ? SOURCE_TAG[q._quoteSource] : null;

        return (
          <Space size={6} wrap>
            <Text strong>${p.toFixed(2)}</Text>
            {tag ? (
              <Tag color={tag.color} style={{ margin: 0 }}>
                {tag.label}
              </Tag>
            ) : null}
            {!tag && r.status !== 'closed' ? (
              <Tag style={{ margin: 0 }}>库存值</Tag>
            ) : null}
          </Space>
        );
      },
    },
    { title: '数量', dataIndex: 'quantity', width: 80 },
    {
      title: '盈亏',
      width: 120,
      render: (_, r) => {
        const n = getPnl(r).amount;
        return (
          <Text type={n >= 0 ? 'success' : 'danger'}>
            {n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
        );
      },
    },
    {
      title: '比率',
      width: 90,
      render: (_, r) => {
        const n = getPnl(r).ratio;
        return (
          <Text type={n >= 0 ? 'success' : 'danger'}>{n.toFixed(2)}%</Text>
        );
      },
    },
    {
      title: '入场日',
      dataIndex: 'entryDate',
      width: 120,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: '启用',
      dataIndex: 'isActive',
      width: 70,
      render: (v) => (v ? <Tag color="green">是</Tag> : <Tag>否</Tag>),
    },
    {
      title: '操作',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="VIP 交易信号"
        description="VIP 会员登录后点击「Start Exploring」进入暗金风格页面，此处维护买卖记录与页头文案。"
      />

      <Card
        title={<Title level={5} style={{ margin: 0 }}>页头配置</Title>}
        extra={
          <Button type="primary" icon={<SaveOutlined />} loading={savingMeta} onClick={saveMeta}>
            保存页头
          </Button>
        }
      >
        <Form form={metaForm} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="title" label="标题" rules={[{ required: true }]}>
                <Input placeholder="VIP Trading Strategy" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="说明文案">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card
        title={<Title level={5} style={{ margin: 0 }}>买卖记录</Title>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchList}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              新增信号
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={filtered}
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 15 }}
        />
      </Card>

      <Modal
        open={modalOpen}
        title={editing ? '编辑信号' : '新增信号'}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onOk={handleSubmit}
        okText="保存"
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="category" hidden initialValue="trading">
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={12} md={8}>
              <Form.Item name="symbol" label="股票代码" rules={[{ required: true }]}>
                <Input placeholder="CRWD" style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="region" label="市场" rules={[{ required: true }]}>
                <Select>
                  <Option value="USA">USA</Option>
                  <Option value="NASDAQ">NASDAQ</Option>
                  <Option value="NYSE">NYSE</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="status" label="仓位状态" rules={[{ required: true }]}>
                <Select>
                  <Option value="active">持仓中 (Current)</Option>
                  <Option value="closed">已平仓 (Exit)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="quantity" label="数量 Qty" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="entryPrice" label="入场价 Entry" rules={[{ required: true }]}>
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="currentPrice" label="现价 Current" extra="持仓中用">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="exitPrice" label="出场价 Exit" extra="已平仓用">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="entryDate" label="入场日期" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="sortOrder" label="排序（大靠前）">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="isActive" label="前台显示" valuePropName="checked">
                <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="备注">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
          <Text type="secondary">盈亏 = (出场价 或 现价 − 入场价) × 数量；比率 = (出场价 或 现价 − 入场价) / 入场价 × 100%。</Text>
        </Form>
      </Modal>
    </Space>
  );
};

export default VipSignalsManage;
